import { select, Store } from "@ngrx/store";
import { ApplicationState } from "../../store/application-store";
import { Injectable } from "@angular/core";
import {concatMap, Observable } from "rxjs";
import { State as OverviewState } from "../reducers/overview";
import {
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  take,
  tap,
} from "rxjs";
import { IOverviewConfigurationModel } from "../models/overview-configuration-model";
import {
  getOverviewTiles,
  selectLabelState,
  selectOverviewState,
} from "../selectors/overview";
import * as overviewActions from "../actions/overview";
import { RouteContextItemType } from "@context/old_index";
import { OverviewHttpService } from "@feature/overview/services/overview-http.service";
import { IOverviewTileModel } from "@feature/overview/models/overview-tile-model";
import { UserDashboardHttpService } from "@dashboards/services/user-dashboard-http.service";
import { Dashboard } from "@dashboards/models/dashboard";
import { IOverviewTilesModel } from "../models/overview-tiles-model";
import { EntityKey } from "@feature/pma-entity/pma-entity-types";
import {ButtonWidget} from "@feature/overview";

@Injectable()
export class OverviewService {
  constructor(
    private readonly store: Store<ApplicationState>,
    private readonly overviewHttpService: OverviewHttpService,
    private readonly userDashboardHttpService: UserDashboardHttpService,
  ) {}

  private loadOrGetConfig() {
    return this.store.pipe(
      select(selectOverviewState),
      tap((state: OverviewState) => {
        if (state.loading === false
            && state.configuration == undefined
            && !state.error) {
          this.loadOverviewConfiguration();
        }
      }),
    );
  }

  getOverviewConfiguration(): Observable<IOverviewConfigurationModel> {
    return this.loadOrGetConfig().pipe(
      map((state) => state.configuration),
      filter(config => config != undefined),
      distinctUntilChanged(),
    );
  }

  getOverviewTilesByProductName(
    productName: string,
  ): Observable<IOverviewTilesModel> {
    const source = combineLatest([
      this.overviewHttpService.getOverviewConfiguration(productName),
      this.store.pipe(select(selectLabelState)),
      this.userDashboardHttpService.getAvailableDashboardByProduct(productName),
    ]);
    return source.pipe(
      take(1),
      map(([configuration, , dashboards]) => {
        if (configuration != undefined) {
          const tiles = dashboards.map((dashboard: Dashboard) => {
            return {
              Name: dashboard.DashboardIdentifier,
              Label: dashboard.Label,
              EntityName: dashboard.EntityName
            } as IOverviewTileModel;
          });
          return {
            AllTiles: tiles,
            AvailableTiles: configuration.AvailableTiles,
            SelectedFilterField: configuration.SelectedFilterField,
            SelectedFilterFieldItems: configuration.SelectedFilterFieldItems

          };
        }
        return undefined;
      }),
    );
  }

  getOverviewTiles(url: string, routeContextItemType: RouteContextItemType): Observable<Array<ButtonWidget>> {
    this.loadOverviewConfiguration();
    return this.store.pipe(
      select(getOverviewTiles(url, routeContextItemType ?? 'None'))
    );
  }

  loadOverview(entityKey: EntityKey) {
    this.store.dispatch(new overviewActions.Load(entityKey));
  }

  loadOverviewConfiguration() {
    this.store.dispatch(new overviewActions.LoadConfiguration());
  }
}
